import { Select, SelectProps } from 'antd';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocaleType } from 'constants/languages';
import { RTL_LANGUAGES } from 'constants/translations';
import { createTag, fetchTags, TagRow } from 'services/api/tags';

import { StyledCreateItem } from './styled';

interface IProps
  extends Omit<SelectProps, 'options' | 'mode' | 'loading' | 'direction'> {
  /**
   * Locale whose tag records to offer. Tags are per-language rows, so
   * each locale tab gets its own picker with its own set of ids.
   */
  language: LocaleType;
}

const BlogTagsSelect: FC<IProps> = ({ language, value, onChange, ...rest }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<TagRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        const res = await fetchTags(language);
        if (!cancelled) setOptions(res.data?.data ?? []);
      } catch {
        // swallow — dropdown stays empty on failure
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [language, refreshKey]);

  const hasExactMatch = useMemo(() => {
    if (!search) return false;

    return options.some(
      tag => tag.name.toLowerCase() === search.trim().toLowerCase()
    );
  }, [search, options]);

  // Tags are created in the locale being edited, then immediately
  // selected — otherwise the editor has to hunt for the row they just
  // made. Append optimistically so the new id has a label to render;
  // only fall back to a full refetch if the response came back without
  // a usable row.
  const onCreateTag = useCallback(async () => {
    const name = search.trim();
    if (!name) return;

    setSearch('');

    try {
      const created = (await createTag({ lang: language, name })).data;

      if (created?.id) {
        setOptions(prev => [...prev, created]);
        onChange?.([...((value as number[]) ?? []), created.id], []);
      } else {
        setRefreshKey(k => k + 1);
      }
    } catch {
      // swallow — tag not created, selection left untouched
    }
  }, [search, language, value, onChange]);

  // `searchValue` is controlled, so antd's own post-select clearing
  // doesn't happen — do it here or the filter stays applied.
  const onSelectionChange = useCallback<NonNullable<SelectProps['onChange']>>(
    (next, option) => {
      setSearch('');
      onChange?.(next, option);
    },
    [onChange]
  );

  return (
    <Select
      {...rest}
      value={value}
      onChange={onSelectionChange}
      mode="multiple"
      loading={isLoading}
      direction={RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr'}
      style={{ width: '100%' }}
      searchValue={search}
      onSearch={setSearch}
      optionFilterProp="label"
      options={options.map(item => ({ label: item.name, value: item.id }))}
      dropdownRender={menu => (
        <>
          {menu}
          {!!search.trim() && !hasExactMatch && (
            <StyledCreateItem onClick={onCreateTag}>
              {search.trim()} {t('blog.create_new_tag')}
            </StyledCreateItem>
          )}
        </>
      )}
    />
  );
};

export default BlogTagsSelect;
